// =============================================================================
// VandieZ shipper-web -- AppShell (sidebar-first shell)
// =============================================================================
// Mirrors admin-web's AdminLayout, lighter (no count badges, no global
// search). One left sidebar holds every piece of chrome:
//
//   Top         brand (collapsible toggle)
//   Middle      navigation links with an emerald active-edge bar
//   Bottom      Logout button, Light/Dark theme pill, user profile card
//
// Mobile: sidebar overlays from the left via the hamburger in the
// mobile top bar.
// Theme: light/dark toggle on the bottom pill flips the `.dark` class
//        via the ThemeProvider; CSS variables already support both.
//
// `App.tsx` mounts <AppShell/> as a layout route; pages render bare
// content inside <Outlet/>.
// =============================================================================

import {
  Bell,
  ChevronsLeft,
  ClipboardCheck,
  HeadphonesIcon,
  Landmark,
  MapPin,
  LogOut,
  Menu,
  Moon,
  Package,
  Plus,
  Scale,
  Star,
  Sun,
  Truck,
  User as UserIcon,
  Wallet,
  X,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { useCollapsed, useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Navigation definition
// ---------------------------------------------------------------------------
type IconComponent = typeof Package;

interface NavItem {
  to: string;
  label: string;
  icon: IconComponent;
  // Extra URL prefixes that should ALSO light this item up. The default
  // NavLink behaviour highlights when the current path STARTS with `to`,
  // which fails for detail routes that share a /loads/* parent with a
  // list at /. List those siblings here.
  activePrefixes?: string[];
}

const NAV_ITEMS: NavItem[] = [
  // The landing page renders the loads list; treat /loads/* (detail +
  // /loads/new) as the same surface so the nav item stays lit.
  { to: "/", label: "My loads", icon: Package, activePrefixes: ["/loads"] },
  { to: "/loads/new", label: "Post load", icon: Plus },
  // Vehicle detail routes light up the same nav item.
  {
    to: "/vehicles",
    label: "Vehicles",
    icon: Truck,
    activePrefixes: ["/vehicles/"],
  },
  { to: "/wallet", label: "Wallet", icon: Wallet },
  { to: "/bank-accounts", label: "Bank accounts", icon: Landmark },
  { to: "/addresses", label: "Addresses", icon: MapPin },
  { to: "/kyc", label: "KYC", icon: ClipboardCheck },
  { to: "/profile", label: "Profile", icon: UserIcon },
  // Disputes detail routes live under /disputes/:id.
  { to: "/disputes", label: "Disputes", icon: Scale, activePrefixes: ["/disputes/"] },
  { to: "/ratings", label: "Ratings", icon: Star },
  { to: "/inbox", label: "Inbox", icon: Bell },
  // Tickets detail routes live under /support/:id.
  { to: "/support", label: "Support", icon: HeadphonesIcon, activePrefixes: ["/support/"] },
];

// ---------------------------------------------------------------------------
// Sidebar internals
// ---------------------------------------------------------------------------
function Sidebar({
  mobileOpen,
  onCloseMobile,
}: {
  mobileOpen: boolean;
  onCloseMobile: () => void;
}): React.JSX.Element {
  const { user, logout } = useAuth();
  const { collapsed, toggle: toggleCollapsed } = useCollapsed();
  const { theme, setTheme } = useTheme();

  return (
    <aside
      className={cn(
        "border-r bg-background flex flex-col shrink-0",
        // Mobile (<md): off-canvas drawer overlay. Sits ABOVE the
        // backdrop (z-40) so taps inside the drawer don't fall through
        // to the close-on-backdrop button behind it.
        "fixed inset-y-0 left-0 z-50 transition-transform duration-200",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // md and up: in-flow sticky sidebar, hidden translateX so it
        // stays visible regardless of the mobileOpen flag.
        "md:sticky md:top-0 md:translate-x-0 md:h-screen md:transition-[width] md:z-auto",
        collapsed ? "md:w-16" : "md:w-64",
        "w-64",
        "h-screen overflow-y-auto",
      )}
      aria-label="Shipper navigation"
      onClick={(e) => { e.stopPropagation(); }}
    >
      {/* Brand + collapse toggle */}
      <div
        className={cn(
          "flex items-center px-3 py-4 border-b",
          collapsed ? "md:justify-center" : "justify-between",
        )}
      >
        {!collapsed && (
          <Link to="/" className="flex items-center gap-2">
            <Truck className="h-6 w-6 text-primary" />
            <span className="text-lg font-semibold tracking-tight">
              VandieZ
            </span>
          </Link>
        )}
        {collapsed && (
          <Link to="/" className="hidden md:block">
            <Truck className="h-6 w-6 text-primary" />
          </Link>
        )}
        {!collapsed && (
          <button
            type="button"
            aria-label="Collapse sidebar"
            className="hidden md:block p-1 rounded hover:bg-muted text-muted-foreground"
            onClick={toggleCollapsed}
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          aria-label="Close menu"
          className="md:hidden p-1 rounded hover:bg-muted text-muted-foreground"
          onClick={onCloseMobile}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-0.5">
          {NAV_ITEMS.map((item) => (
            <NavRow
              key={item.to}
              item={item}
              collapsed={collapsed}
              onNavigate={onCloseMobile}
            />
          ))}
        </ul>
      </nav>

      {/* Bottom block: Logout + theme toggle + user card */}
      <div className="border-t px-2 py-3 space-y-1">
        <button
          type="button"
          onClick={() => void logout()}
          className={cn(
            "w-full flex items-center rounded-md text-sm",
            "hover:bg-muted text-foreground",
            collapsed ? "justify-center p-2" : "gap-2 px-2 py-1.5",
          )}
          aria-label="Sign out"
          title={collapsed ? "Sign out" : undefined}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>

        {/* Theme pill */}
        {!collapsed ? (
          <div className="mt-2 rounded-full border bg-muted/40 p-0.5 flex text-xs">
            <button
              type="button"
              onClick={() => { setTheme("light"); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 rounded-full py-1 transition-colors",
                theme === "light"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground",
              )}
              aria-pressed={theme === "light"}
              aria-label="Light theme"
            >
              <Sun className="h-3.5 w-3.5" /> Light
            </button>
            <button
              type="button"
              onClick={() => { setTheme("dark"); }}
              className={cn(
                "flex-1 flex items-center justify-center gap-1 rounded-full py-1 transition-colors",
                theme === "dark"
                  ? "bg-background shadow-sm font-medium"
                  : "text-muted-foreground",
              )}
              aria-pressed={theme === "dark"}
              aria-label="Dark theme"
            >
              <Moon className="h-3.5 w-3.5" /> Dark
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setTheme(theme === "dark" ? "light" : "dark"); }}
            className="w-full flex items-center justify-center p-2 rounded-md hover:bg-muted text-muted-foreground"
            aria-label="Toggle theme"
            title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
        )}

        {/* User profile card */}
        <div
          className={cn(
            "mt-2 border-t pt-3 flex items-center",
            collapsed ? "justify-center" : "gap-2 px-1",
          )}
        >
          <Avatar name={user?.full_name ?? "?"} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">
                {user?.full_name ?? "—"}
              </div>
              <div className="text-xs text-muted-foreground truncate font-mono">
                {user?.phone ?? ""}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}): React.JSX.Element {
  const location = useLocation();
  const path = location.pathname;
  const isActive =
    item.to === "/"
      ? path === "/" || (item.activePrefixes?.some((p) => path.startsWith(p)) ?? false)
      : path === item.to ||
        path.startsWith(`${item.to}/`) ||
        (item.activePrefixes?.some((p) => path.startsWith(p)) ?? false);

  return (
    <li>
      <NavLink
        to={item.to}
        end={item.to === "/"}
        onClick={onNavigate}
        className={cn(
          "group relative flex items-center rounded-md text-sm",
          "hover:bg-muted",
          collapsed ? "justify-center h-10" : "gap-2.5 px-2.5 h-10",
          isActive
            ? "bg-primary-subtle text-primary-subtle-foreground font-medium"
            : "text-foreground",
        )}
        title={collapsed ? item.label : undefined}
      >
        {isActive && (
          <span
            className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-primary"
            aria-hidden
          />
        )}
        <item.icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      </NavLink>
    </li>
  );
}

function Avatar({ name }: { name: string }): React.JSX.Element {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("") || "?";
  return (
    <div
      className="h-8 w-8 rounded-full bg-primary-subtle text-primary-subtle-foreground flex items-center justify-center text-xs font-semibold shrink-0"
      aria-hidden
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout root + page header helper
// ---------------------------------------------------------------------------
// Providers (Theme, SidebarCollapse, Toast) are mounted in App.tsx so the
// public /login page also gets dark-mode + toasts. AppShell itself is
// just the layout — no providers — to avoid double-mount.
export function AppShell(): React.JSX.Element {
  return <AppShellInner />;
}

function AppShellInner(): React.JSX.Element {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen bg-muted/30">
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => { setMobileOpen(false); }}
        />
      )}

      <Sidebar
        mobileOpen={mobileOpen}
        onCloseMobile={() => { setMobileOpen(false); }}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar with hamburger (hidden on md+) */}
        <header className="sticky top-0 z-20 bg-background border-b px-3 py-2 flex items-center gap-2 md:hidden">
          <button
            type="button"
            aria-label="Open navigation"
            className="p-1.5 rounded hover:bg-muted"
            onClick={() => { setMobileOpen(true); }}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            <span className="font-semibold">VandieZ</span>
          </Link>
        </header>

        <main className="flex-1 px-4 py-6 md:px-8">
          <div className="mx-auto max-w-5xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Lightweight page header used inside the <Outlet>.
 *   <PageHeader title="..." description="..." actions={...} />
 *
 * Mobile (<sm): title + actions stack vertically, actions row wraps and
 * each button stretches via `flex-1` so a single CTA goes full-width.
 * sm and up: side-by-side, actions right-aligned (the original look).
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h1>
        {description !== undefined && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {actions !== undefined && (
        <div className="flex flex-wrap items-center gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
          {actions}
        </div>
      )}
    </div>
  );
}
